import type { WorkspaceRole as Role } from "@minga/protocol"

/** Pure helpers for workspaces: names, slugs, roles (earlier settings spec §1.3, §4.3, §5.3). */

export const ROLE_LABEL: Record<Role, string> = { owner: "Owner", admin: "Admin", member: "Member" }

/** The highest role in Better Auth's comma list ("member,admin" → "admin"); unknown roles → "member". */
export const highestRole = (roles: string | null | undefined): Role => {
  const list = (roles ?? "").split(",").map((r) => r.trim())
  if (list.includes("owner")) return "owner"
  if (list.includes("admin")) return "admin"
  return "member"
}

export const slugify = (name: string): string =>
  name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "workspace"

/** `slugify(name)-{4 random base36}`, as the picker and /welcome create workspaces. */
export const newWorkspaceSlug = (name: string, random: () => number = Math.random): string => {
  const suffix = Math.floor(random() * 36 ** 4)
    .toString(36)
    .padStart(4, "0")
    .slice(-4)
  return `${slugify(name)}-${suffix}`
}

export const WORKSPACE_SLUG = /^[a-z0-9][a-z0-9-]{1,46}[a-z0-9]$/

/** Problems with a workspace name or slug (earlier spec §4.3): name 1–48, slug pattern. */
export const workspaceProblems = (input: {
  readonly name: string
  readonly slug: string
}): Partial<Record<"name" | "slug", string>> => {
  const out: Partial<Record<"name" | "slug", string>> = {}
  const name = input.name.trim()
  if (!name) out.name = "Give the workspace a name."
  else if (name.length > 48) out.name = "At most 48 characters."
  if (!WORKSPACE_SLUG.test(input.slug))
    out.slug = "3–48 lowercase letters, digits and dashes; no dash at the start or end."
  return out
}

/** "J…@gmail.com": the first character, an ellipsis and the domain (lists never show a full address). */
export const maskEmail = (email: string | null | undefined): string => {
  if (!email) return ""
  const at = email.lastIndexOf("@")
  if (at <= 0) return email
  return `${email[0]}…@${email.slice(at + 1)}`
}

/** "Good morning / afternoon / evening, Jannik." in the browser's time zone. */
export const greeting = (name: string | null | undefined, hour: number): string => {
  const part = hour < 5 ? "evening" : hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening"
  const first = (name ?? "").trim().split(/\s+/)[0]
  return `Good ${part}${first ? `, ${first}` : ""}.`
}
